import { useCallback, useEffect, useMemo, useState } from "react";
import { emplacarAPI } from "../../servicos/emplacar";
import { useNavigate } from "react-router-dom";
import Topo from "../Topo/topo";
import ListagemFeedback from "../Listagem/feedback";
import ListagemOpcoes from "../Listagem/opcoes";
import Paginacao from "../Listagem/paginacao";
import UsuarioCard from "./card";
import { DataTable } from "../ui/data-table";
import ColumnToggleDropdown from "../ui/column-toggle-dropdown";
import { useLoteProgress } from "./Lote/LoteProgressContext";

const IPVA_WARNING_DEFAULT = "DETRANPR: IPVA não disponível ou já foi pago.";

const normalizarMensagem = (valor) => {
    if (typeof valor !== "string") {
        return "";
    }

    const texto =
        typeof valor.normalize === "function"
            ? valor.normalize("NFD")
            : valor;

    return texto.replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const mensagemIndicaIpvaSemRetorno = (mensagem) => {
    const texto = normalizarMensagem(mensagem);

    if (!texto || !texto.includes("ipva")) {
        return false;
    }

    return (
        texto.includes("vazi") ||
        texto.includes("sem retorno") ||
        texto.includes("sem resposta") ||
        texto.includes("sem informacao") ||
        texto.includes("nao retornou") ||
        texto.includes("response empty")
    );
};

const SITUACAO_CONFIG = {
    "DETRANPR: VEICULO JA EMPLACADO NA BIN. (1043)": { color: "#EA3323", icon: "Error" },
    "DetranPR informa que a Fábrica/Concessionário ainda não realizou o comunicado de venda. Entre em contato com o vendedor. (RENAVE)": { color: "#A96424", icon: "Warning" },
    "Processo de emplacamento criado. Aguardando pagamento da guia.": { color: "#22C55E", icon: "price_check" },
    "DetranPR: Pagamento ainda não identificado/processado. Aguarde 2 horas para confirmação do PIX.": { color: "#A96424", icon: "price_check" },
    "Documentos Gerados com Sucesso.": { color: "#22C55E", icon: "done_all" },
};

const resolverEstiloSituacao = (descricao) => SITUACAO_CONFIG[descricao] || null;

function EmplacarTabela() {

    const breadcrumbs = ['Home', 'Emplacar'];

    const { registrarProcessosExistentes } = useLoteProgress();

    const [exibeErro, setExibeErro] = useState(false);
    const [mensagemErro, setMensagemErro] = useState('');

    const [exibeSucesso, setExibeSucesso] = useState(false);
    const [mensagemSucesso, setMensagemSucesso] = useState('');

    const [exibeAviso, setExibeAviso] = useState(false);
    const [mensagemAviso, setMensagemAviso] = useState('');

    const [modalFinalizarAberto, setModalFinalizarAberto] = useState(false);
    const [registroFinalizacao, setRegistroFinalizacao] = useState(null);
    const [finalizando, setFinalizando] = useState(false);

    const limparMensagens = useCallback(() => {
        setExibeSucesso(false);
        setMensagemSucesso('');
        setExibeErro(false);
        setMensagemErro('');
        setExibeAviso(false);
        setMensagemAviso('');
    }, []);

    const exibirAvisoIpva = useCallback((mensagem) => {
        setExibeAviso(true);
        setMensagemAviso(mensagem || IPVA_WARNING_DEFAULT);
    }, []);

    const tratarIpvaSemRetorno = useCallback((erro) => {
        const mensagem = erro?.response?.data?.message;
        if (mensagemIndicaIpvaSemRetorno(mensagem)) {
            exibirAvisoIpva();
            return true;
        }
        return false;
    }, [exibirAvisoIpva]);

    const [PesquisaVeiculo, setPesquisaVeiculo] = useState([]);
    const [filtroTexto, setFiltroTexto] = useState("");

    const fetchVeiculos = useCallback(async () => {

        const fi100_key_public = sessionStorage.getItem('key');
        emplacarAPI.get(`/${fi100_key_public}`)
            .then(resposta => {
                const registros = Array.isArray(resposta.data) ? resposta.data : [];
                setPesquisaVeiculo(registros);
                registrarProcessosExistentes(registros);
            })
            .catch(erro => {
                if (erro?.resposta?.data?.message) {
                    setExibeErro(true);
                    setMensagemErro(erro.resposta.data.message);
                } else {
                    if (erro.response?.status === 401) {
                        setExibeErro(true);
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setExibeErro(true);
                        setMensagemErro('Ops! Ocorreu um erro inesperado.');
                    }
                }
            })

    }, [registrarProcessosExistentes]);

    useEffect(() => {
        fetchVeiculos();
    }, [fetchVeiculos]);

    const navigate = useNavigate();

    const DesativarEmplacamento = useCallback(async (key) => {
        limparMensagens();

        emplacarAPI.patch(`/desativar/${key}`)
            .then(resposta => {
                setExibeSucesso(true);
                setMensagemSucesso('Processo desativado com sucesso.');
                fetchVeiculos();
            })
            .catch(erro => {
                if (erro?.resposta?.data?.message) {
                    setExibeErro(true);
                    setMensagemErro(erro.resposta.data.message);
                } else {
                    if (erro.response?.status === 401) {
                        setExibeErro(true);
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setExibeErro(true);
                        setMensagemErro('Ops! Ocorreu um erro inesperado.');
                    }

                }
            });
    }, [fetchVeiculos, limparMensagens]);
    
    const FinalizarEmplacamento = useCallback(async (key) => {
        limparMensagens();

        try {
            await emplacarAPI.patch(`/Finalizar/${key}`);
            setExibeSucesso(true);
            setMensagemSucesso('Processo finalizado com sucesso.');
            fetchVeiculos();
            return true;
        } catch (erro) {
            if (erro?.resposta?.data?.message) {
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            } else {
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                } else {
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                }

            }
            return false;
        }
    }, [fetchVeiculos, limparMensagens]);

    const abrirModalFinalizar = useCallback((registro) => {
        setRegistroFinalizacao(registro);
        setModalFinalizarAberto(true);
    }, []);

    const fecharModalFinalizar = useCallback(() => {
        setRegistroFinalizacao(null);
        setModalFinalizarAberto(false);
        setFinalizando(false);
    }, []);

    const confirmarFinalizacao = useCallback(async () => {
        if (!registroFinalizacao?.ve101_key_public) {
            return;
        }
        setFinalizando(true);
        const finalizado = await FinalizarEmplacamento(registroFinalizacao.ve101_key_public);
        setFinalizando(false);
        if (finalizado) {
            fecharModalFinalizar();
        }
    }, [FinalizarEmplacamento, fecharModalFinalizar, registroFinalizacao]);

    const EnviarDetran = useCallback(async (id) => {
        limparMensagens();

        emplacarAPI.get(`/Processo/${id}`)
            .then(resposta => {
                setExibeSucesso(true);
                setMensagemSucesso('Processo (' + resposta.data.emplacar.ve101_num_processo + ') criado com sucesso. Clique no número para salvar a guia.');
                fetchVeiculos();
            })
            .catch(erro => {
                if (tratarIpvaSemRetorno(erro)) {
                    fetchVeiculos();
                    return;
                }
                if (erro?.response?.data?.message) {
                    setExibeErro(true);
                    if ((erro.response?.status === 401) || ((erro.response?.status === 406) && erro?.response?.data?.message === "Token Inválido") ) {                        
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setMensagemErro(erro.response.data.message);
                    }
                } else {
                    if (erro.response?.status === 401) {
                        setExibeErro(true);
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setExibeErro(true);
                        setMensagemErro('Ops! Ocorreu um erro inesperado.');
                    }
                }
                fetchVeiculos();
            });
    }, [fetchVeiculos, limparMensagens, tratarIpvaSemRetorno]);

    const BuscarDocumentos = useCallback(async (id) => {
        limparMensagens();

        emplacarAPI.get(`/Documento/${id}`)
            .then(resposta => {
                setExibeSucesso(true);
                setMensagemSucesso('Processo (' + resposta.data.emplacar.ve101_num_processo + ') criado com sucesso. Clique no botão de Download.');
            })
            .catch(erro => {
                if (tratarIpvaSemRetorno(erro)) {
                    fetchVeiculos();
                    return;
                }
                setExibeErro(true);
                if (erro?.response?.data?.message) {                    
                    if ((erro.response?.status === 401) || ((erro.response?.status === 406) && erro?.response?.data?.message === "Token Inválido") ) {                        
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setMensagemErro(erro.response.data.message);
                    }
                } else {
                    if (erro.response?.status === 401) {
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setMensagemErro('Ops! Ocorreu um erro inesperado.');
                    }
                }
                fetchVeiculos();
            });                
    }, [fetchVeiculos, limparMensagens, tratarIpvaSemRetorno]);    

    const ReprocessarDocumentos = useCallback(async (id) => {
        limparMensagens();

        emplacarAPI.post(`/documento/recuperar/${id}`)
            .then(resposta => {
                setExibeSucesso(true);
                setMensagemSucesso(resposta?.data?.message || 'Documentos reprocessados com sucesso.');
                fetchVeiculos();
            })
            .catch(erro => {
                if (tratarIpvaSemRetorno(erro)) {
                    return;
                }
                setExibeErro(true);
                if (erro?.response?.data?.message) {
                    if ((erro.response?.status === 401) || ((erro.response?.status === 406) && erro?.response?.data?.message === "Token Inválido")) {
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setMensagemErro(erro.response.data.message);
                    }
                } else if (erro.response?.status === 401) {
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                } else {
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                }
            });
    }, [fetchVeiculos, limparMensagens, tratarIpvaSemRetorno]);

    const ReprocessarProcesso = useCallback(async (id) => {
        limparMensagens();

        emplacarAPI.post(`/processo/recuperar/${id}`)
            .then(resposta => {
                setExibeSucesso(true);
                setMensagemSucesso(resposta?.data?.message || 'Guia do processo recuperada com sucesso.');
                fetchVeiculos();
            })
            .catch(erro => {
                if (tratarIpvaSemRetorno(erro)) {
                    return;
                }
                setExibeErro(true);
                if (erro?.response?.data?.message) {
                    if ((erro.response?.status === 401) || ((erro.response?.status === 406) && erro?.response?.data?.message === "Token Inválido")) {
                        setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                    } else {
                        setMensagemErro(erro.response.data.message);
                    }
                } else if (erro.response?.status === 401) {
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                } else {
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                }
            });
    }, [fetchVeiculos, limparMensagens, tratarIpvaSemRetorno]);

    const Editar = useCallback((key) => {
        let path = `/filiado/emplacar/editar/${key}`;
        navigate(path);
    }, [navigate]);    
    
    const FormatarDataHora = useCallback((string) => {
        if (!string) {
            return '';
        }
        const [dataBruta, horaBruta] = string.split("T");
        if (!dataBruta || !horaBruta) {
            return string;
        }
        const [ano, mes, dia] = dataBruta.split("-");
        const hora = horaBruta.substring(0, 5);
        return `${dia}/${mes}/${ano} ${hora}`;
    }, []);

    const renderDocumentoIcone = useCallback((url, icon, titulo) => {
        if (url) {
            return (
                <a href={url} target="_blank" rel="noreferrer" title={titulo}>
                    <span class="material-symbols-outlined">{icon}</span>
                </a>
            );
        }
        return (
            <span class="tw-text-slate-400 tw-opacity-60" title={`${titulo} indisponível`}>
                <span class="material-symbols-outlined">{icon}</span>
            </span>
        );
    }, []);

    const colunasBase = useMemo(
        () => [
            {
                id: 'sequencia',
                header: () => 'Seq/Data',
                label: 'Seq/Data',
                canHide: false,
                className: 'tw-align-top tw-text-sm tw-font-semibold tw-whitespace-nowrap',
                cell: ({ row }) => (
                    <div class="tw-flex tw-flex-col tw-gap-1">
                        <span class="tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{row.ve101_cod_emplacar}</span>
                        <span class="email">{FormatarDataHora(row.createdAt)}</span>
                    </div>
                ),
            },
            {
                id: 'nfChassi',
                header: () => 'NF/Chassi',
                label: 'NF/Chassi',
                className: 'tw-align-top tw-break-words',
                cell: ({ row }) => (
                    <div class="tw-flex tw-flex-col tw-gap-1">
                        <span class="tw-font-medium tw-text-slate-700 dark:tw-text-slate-100">{row.ve101_key_nfe}</span>
                        <span class="email tw-uppercase">{row.ve101_dsc_chassi}</span>
                    </div>
                ),
            },
            {
                id: 'processo',
                header: () => 'Processo',
                label: 'Processo',
                className: 'tw-align-top',
                cell: ({ row }) => (
                    row.ve101_num_processo ? (
                        <a href={row.ve101_url_processo} target="_blank" rel="noreferrer" class="tw-text-indigo-600 hover:tw-underline">
                            {row.ve101_num_processo}
                        </a>
                    ) : (
                        <span class="tw-text-slate-500">-</span>
                    )
                ),
            },
            {
                id: 'documentos',
                header: () => 'Documentos',
                label: 'Documentos',
                className: 'tw-align-top',
                cell: ({ row }) => {
                    return (
                        <>
                            <div class="tw-flex tw-items-center tw-gap-2">
                                {renderDocumentoIcone(row.ve101_url_autorizacao, "Inventory", "Autorização")}
                                {renderDocumentoIcone(row.ve101_url_crv, "Clarify", "CRLV-e")}
                                {renderDocumentoIcone(row.ve101_url_seguranca, "Lock", "Código de Segurança")}
                                {renderDocumentoIcone(row.ve101_url_ipva, "request_quote", "Guia IPVA")}
                            </div>
                        </>
                    );
                },
            },
            {
                id: 'status',
                header: () => 'Data/Status',
                label: 'Data/Status',
                className: 'tw-align-top',
                cell: ({ row }) => {
                    const situacao = row.ve101_dsc_situacao || '-';
                    const aguardandoIpva =
                        situacao === "Documentos Gerados com Sucesso." && !row.ve101_url_ipva;
                    const textoStatus = aguardandoIpva
                        ? "Gerando documentos (aguardando IPVA)"
                        : situacao;
                    const estilo = aguardandoIpva
                        ? { color: "#A96424", icon: "hourglass_top" }
                        : resolverEstiloSituacao(textoStatus);
                    return (
                        <div class="tw-flex tw-flex-col tw-gap-1">
                            <span class="email">{FormatarDataHora(row.ve101_dat_situacao)}</span>
                            <span class="email" style={estilo?.color ? { color: estilo.color } : undefined}>
                                {estilo?.icon && (
                                    <span class="material-symbols-outlined" style={{ color: estilo.color, verticalAlign: 'bottom' }}>
                                        {estilo.icon}
                                    </span>
                                )}
                                {textoStatus}
                            </span>
                        </div>
                    );
                },
            },
            {
                id: 'acoes',
                header: () => '',
                label: 'Ações',
                canHide: false,
                className: 'tw-w-1 tw-text-right',
                cell: ({ row }) => (
                    <div class="dropdown">
                        <button class="dropdown-acao btn-dropdown btn btn-secondary" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <span class="material-symbols-outlined">more_vert</span>
                        </button>
                        <ul class="dropdown-menu">
                            <li>
                                {row.ve101_url_processo === null &&
                                    <button type="button" class="dropdown-item align-icon" onClick={() => Editar(row.ve101_key_public)}>
                                        <span class="material-symbols-outlined">edit</span>Editar
                                    </button>
                                }
                                {row.ve101_url_processo === null &&
                                    <button type="button" class="dropdown-item align-icon" onClick={() => EnviarDetran(row.ve101_key_public)}>
                                        <span class="material-symbols-outlined">send</span>Registrar
                                    </button>
                                }
                                {row.ve101_url_processo === null &&
                                    <button type="button" class="dropdown-item align-icon action-disable" onClick={() => DesativarEmplacamento(row.ve101_key_public)}>
                                        <span class="material-symbols-outlined">do_not_disturb_on</span>Desativar
                                    </button>
                                }
                                {row.ve101_url_processo !== null &&
                                    !(
                                        row.ve101_url_autorizacao ||
                                        row.ve101_url_crv ||
                                        row.ve101_url_seguranca ||
                                        row.ve101_url_ipva
                                    ) ? (
                                    <button type="button" class="dropdown-item align-icon" onClick={() => BuscarDocumentos(row.ve101_key_public)}>
                                        <span class="material-symbols-outlined">contract</span>Documentos
                                    </button>
                                ) : null}
                                {row.ve101_url_processo !== null &&
                                    (row.ve101_url_autorizacao ||
                                        row.ve101_url_crv ||
                                        row.ve101_url_seguranca ||
                                        row.ve101_url_ipva) && (
                                    <button type="button" class="dropdown-item align-icon" onClick={() => ReprocessarDocumentos(row.ve101_key_public)}>
                                        <span class="material-symbols-outlined">refresh</span>Reprocessar documentos
                                    </button>
                                )}
                                <button type="button" class="dropdown-item align-icon" onClick={() => abrirModalFinalizar(row)}>
                                    <span class="material-symbols-outlined">done_all</span>Finalizar
                                </button>
                                {row.ve101_url_processo !== null &&
                                    <button type="button" class="dropdown-item align-icon" onClick={() => ReprocessarProcesso(row.ve101_key_public)}>
                                        <span class="material-symbols-outlined">picture_as_pdf</span>Recuperar guia do processo
                                    </button>
                                }
                            </li>
                        </ul>
                    </div>
                ),
            },
        ],
        [FormatarDataHora, renderDocumentoIcone, Editar, EnviarDetran, DesativarEmplacamento, BuscarDocumentos, ReprocessarDocumentos, ReprocessarProcesso, abrirModalFinalizar],
    );

    const [colunasVisiveis, setColunasVisiveis] = useState(() =>
        Object.fromEntries(colunasBase.map(coluna => [coluna.id, true])),
    );

    const colunasTabela = useMemo(
        () => colunasBase.filter(coluna => colunasVisiveis[coluna.id] !== false),
        [colunasBase, colunasVisiveis],
    );

    const dadosFiltrados = useMemo(() => {
        const busca = filtroTexto.trim().toLowerCase();
        if (!busca) {
            return PesquisaVeiculo;
        }
        return PesquisaVeiculo.filter((item) => {
            const termos = [
                item.ve101_cod_emplacar,
                item.ve101_key_nfe,
                item.ve101_dsc_chassi,
                item.ve101_num_processo,
                item.ve101_dsc_situacao,
            ];
            return termos.some((campo) => (campo || '').toString().toLowerCase().includes(busca));
        });
    }, [PesquisaVeiculo, filtroTexto]);

    const toggleColuna = useCallback((colunaId) => {
        const coluna = colunasBase.find(col => col.id === colunaId);
        if (coluna?.canHide === false) {
            return;
        }
        setColunasVisiveis((anteriores) => ({
            ...anteriores,
            [colunaId]: anteriores[colunaId] === false,
        }));
    }, [colunasBase]);

    return (
        <>
        <div class="row">
            <div class="container">
                <div class="topo-wrapper">
                    <Topo titulo='Emplacar' breadcrumbs={breadcrumbs} btnincluir={true} destino='/filiado/emplacar/novo' admin={false} />
                    <ListagemFeedback
                        displayErro={exibeErro}
                        mensagemErro={mensagemErro}
                        displaySucesso={exibeSucesso}
                        mensagemSucesso={mensagemSucesso}
                        displayAviso={exibeAviso}
                        mensagemAviso={mensagemAviso}
                    />
                </div>
            </div>
            <ListagemOpcoes
                esconderTamanhoPagina={true}
                esconderOpcaoFiltro={true}
                esconderCampoFiltro={true}
            />
            <div class="row row-padding-conteudo">
                <div class="col-12">
                    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/90 tw-p-4 tw-shadow-sm dark:tw-border-slate-700 dark:tw-bg-slate-900/70">
                        <div class="tw-flex tw-min-w-[240px] tw-flex-1 tw-items-center tw-gap-3">
                            <label for="filtro-emplacar" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-slate-300">
                                Pesquisa:
                            </label>
                            <input
                                id="filtro-emplacar"
                                type="search"
                                class="form-control tw-h-11 tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white/95 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 placeholder:tw-text-slate-400 focus:tw-border-indigo-400 focus:tw-ring-2 focus:tw-ring-indigo-300 dark:tw-border-slate-700 dark:tw-!bg-slate-900/70 dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500 dark:focus:tw-ring-indigo-400/40"
                                placeholder="Busque por NF, chassi, processo..."
                                value={filtroTexto}
                                onChange={evento => setFiltroTexto(evento.target.value)}
                            />
                        </div>
                        <div class="tw-flex tw-ml-auto tw-items-center">
                            <ColumnToggleDropdown
                                colunas={colunasBase}
                                visibilidade={colunasVisiveis}
                                onToggle={toggleColuna}
                                label="Colunas"
                                align="end"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row row-padding-conteudo" >
                <div class="col-12 mobile-hide tablet-hide">
                    <div class="admin-emplacar-table-wrapper table-responvive">
                        <DataTable
                            columns={colunasTabela}
                            data={dadosFiltrados}
                            getRowId={(row, index) => row.ve101_key_public || index}
                            className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-lg dark:tw-border-slate-800 dark:tw-bg-slate-900"
                        />
                    </div>
                </div>
            </div>
            <UsuarioCard />
            <Paginacao esconder={true} />
        </div>
        {modalFinalizarAberto && (
            <div class="veiculo-detalhe-modal-backdrop" role="dialog" aria-modal="true">
                <div class="veiculo-detalhe-modal">
                    <div class="veiculo-detalhe-modal__header">
                        <div>
                            <h3>Finalizar processo</h3>
                            <p>Confirme para arquivar o emplacamento.</p>
                        </div>
                        <button type="button" class="btn-icon" onClick={fecharModalFinalizar} disabled={finalizando}>
                            <span class="material-symbols-outlined">close</span>
                        </button>
                    </div>
                    <div class="veiculo-detalhe-modal__body">
                        <div class="tw-space-y-3">
                            {registroFinalizacao?.ve101_cod_emplacar && (
                                <p class="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                                    Processo <strong class="tw-text-slate-800 dark:tw-text-slate-50">{registroFinalizacao.ve101_cod_emplacar}</strong>
                                    {registroFinalizacao?.ve101_dsc_chassi && (
                                        <span class="tw-text-slate-500 dark:tw-text-slate-400"> · Chassi {registroFinalizacao.ve101_dsc_chassi}</span>
                                    )}
                                </p>
                            )}
                            <p class="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                                Ao finalizar, o processo será marcado como concluído/arquivado e sairá do fluxo de ações.
                                Essa operação pode ser realizada mesmo sem os documentos gerados e não poderá ser desfeita.
                            </p>
                            <p class="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                                Deseja finalizar agora?
                            </p>
                        </div>
                        <div class="tw-mt-6 tw-flex tw-justify-end tw-gap-3">
                            <button type="button" class="btn btn-light" onClick={fecharModalFinalizar} disabled={finalizando}>
                                Cancelar
                            </button>
                            <button type="button" class="btn btn-primary-blue-plain tw-flex tw-items-center tw-gap-2" onClick={confirmarFinalizacao} disabled={finalizando}>
                                {finalizando && <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                                Finalizar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    )

}

export default EmplacarTabela;
